import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Printer, Clock3 } from "lucide-react";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JournalPostPage() {
  const { slug } = useParams();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    axios
      .get(`${API}/reflections`)
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]));
  }, [slug]);

  if (posts === null) {
    return (
      <main className="min-h-screen bg-pawc-ink flex items-center justify-center">
        <p className="text-pawc-mist/60 text-sm tracking-[0.25em] uppercase">Slow Water</p>
      </main>
    );
  }

  const post = posts.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/" replace />;

  const others = posts.filter((p) => p.slug !== slug).slice(0, 2);
  const words = post.body.join(" ").split(/\s+/).length;
  const readMins = Math.max(1, Math.ceil(words / 180));

  return (
    <main data-testid="journal-post-page">
      <section className="bg-pawc-ink text-pawc-sand pt-32 pb-20 md:pt-40 md:pb-24 print:bg-white print:text-pawc-ink print:pt-6 print:pb-6">
        <div className="max-w-3xl mx-auto px-6">
          <Link
            data-testid="post-back-link"
            to="/"
            className="print-hidden inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-pawc-mist/60 hover:text-pawc-sand transition-colors duration-300 mb-10"
          >
            <ArrowLeft size={14} /> Slow Water
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full border border-pawc-sand/30 text-pawc-mist/80 print:border-pawc-ink/30 print:text-pawc-slate">
                {post.tag}
              </span>
              <span className="text-xs text-pawc-mist/60 print:text-pawc-slate">{post.date}</span>
              <span
                data-testid="post-reading-time"
                className="inline-flex items-center gap-1.5 text-xs text-pawc-mist/60 print:text-pawc-slate"
              >
                <Clock3 size={13} strokeWidth={1.5} /> {readMins} min read
              </span>
              <button
                data-testid="post-print-btn"
                onClick={() => window.print()}
                className="print-hidden inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border border-pawc-sand/30 hover:border-pawc-sand text-pawc-mist/80 rounded-full px-4 py-1.5 transition-colors duration-300"
              >
                <Printer size={13} /> Print / save as PDF
              </button>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl leading-tight tracking-tight">
              {post.title}
            </h1>
            <p className="hidden print:block text-sm text-pawc-slate mt-4">
              Slow Water · Yielding: Flow and Form · Clive Benhura, Counselling Psychologist
            </p>
          </motion.div>
        </div>
      </section>

      <article className="bg-pawc-sand py-16 md:py-24 print:bg-white print:py-8">
        <div className="max-w-2xl mx-auto px-6">
          {post.body.map((para, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className={`text-pawc-slate leading-[1.85] mb-8 ${
                i === 0 ? "font-serif text-xl md:text-2xl text-pawc-deep" : "text-base md:text-lg"
              }`}
            >
              {para}
            </motion.p>
          ))}
          {post.closing && (
            <motion.blockquote
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="border-l-2 border-pawc-terra pl-6 mt-14"
              data-testid="post-closing-line"
            >
              <p className="font-serif italic text-xl md:text-2xl text-pawc-deep leading-snug">
                {post.closing}
              </p>
            </motion.blockquote>
          )}
        </div>
      </article>

      {others.length > 0 && (
        <section className="print-hidden bg-pawc-paper py-16 md:py-20 border-t border-pawc-teal/10">
          <div className="max-w-3xl mx-auto px-6">
            <p className="text-xs tracking-[0.25em] uppercase text-pawc-teal mb-8">
              More from Slow Water
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/reflections/${p.slug}`}
                  data-testid={`related-post-${p.slug}`}
                  className="group rounded-2xl bg-pawc-sand border border-pawc-teal/10 p-7 hover:-translate-y-1 transition-transform duration-500 block"
                >
                  <span className="text-[10px] tracking-[0.25em] uppercase text-pawc-teal">
                    {p.tag}
                  </span>
                  <h3 className="font-serif text-2xl text-pawc-deep mt-2 mb-3">{p.title}</h3>
                  <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-pawc-teal">
                    Read <ArrowRight size={13} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="print-hidden">
        <Footer />
        <FloatingWhatsApp />
      </div>
    </main>
  );
}
